import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { Liquid } from 'liquid-gooey'
import {
  Plus,
  UserPlus,
  FileText,
  CalendarPlus,
  Receipt,
  CreditCard,
  Contact,
  MessageSquarePlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useCallStore } from '@/stores/callStore'
import { DOCK_PILL_H } from './dockMorph'
import { CREATE_LABELS } from '@/lib/labels'
import { useFabClearance } from './useFabClearance'
import { usePermissions } from '@/hooks/usePermission'
import { useNetworkAction } from '@/components/ui/OfflineActions'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import type { CapabilityName } from '@/lib/permissions'

/**
 * The phone's one create button (UX-093): a "+" in the thumb zone, just above
 * the dock's right end, that bubbles UP into one circle per create action.
 * It used to sit in every page header (top-right, out of reach, scrolling away
 * and crowding each page's own white button), with a fixed fallback that
 * landed on the notification banner (UX-103).
 *
 * Effect: libraries.dev "Gooey" (npm `liquid-gooey`, MIT). The circles share
 * one goo silhouette, so they split off the plus like droplets on open and
 * merge back into it on close. Item motion is a spring (`bouncy`) compiled to
 * CSS linear() easing by the library, which also snaps instantly under
 * prefers-reduced-motion. Labels are plain DOM beside each circle.
 */

export type CreateAction = {
  /** Stable key — also how a page puts its own object first. */
  kind: 'lead' | 'quote' | 'job' | 'payment' | 'invoice' | 'contact' | 'text'
  label: string
  to: string
  icon: React.ComponentType<{ className?: string }>
  /** Hidden for people who can't do it (UX-010). */
  capability: CapabilityName
}

// Existing deep links — each one opens that page's own create flow.
export const CREATE_ACTIONS: CreateAction[] = [
  { kind: 'lead', label: CREATE_LABELS.lead, to: '/work?create=lead', icon: UserPlus, capability: 'createLead' },
  { kind: 'quote', label: CREATE_LABELS.quote, to: '/quotes/builder', icon: FileText, capability: 'createQuote' },
  { kind: 'job', label: CREATE_LABELS.job, to: '/work?create=job', icon: CalendarPlus, capability: 'scheduleJob' },
  // Its own icon: it shared the invoice's receipt (copy-a-51).
  { kind: 'payment', label: CREATE_LABELS.payment, to: '/payments', icon: CreditCard, capability: 'recordPayment' },
  { kind: 'invoice', label: CREATE_LABELS.invoice, to: '/work?create=invoice', icon: Receipt, capability: 'createInvoice' },
  { kind: 'contact', label: CREATE_LABELS.contact, to: '/customers/people?new=1', icon: Contact, capability: 'createContact' },
  { kind: 'text', label: CREATE_LABELS.text, to: '/inbox?compose=1', icon: MessageSquarePlus, capability: 'sendMessage' },
]

/** The create actions this person may use, in menu order. */
export function allowedCreateActions(can: (c: CapabilityName) => boolean): CreateAction[] {
  return CREATE_ACTIONS.filter((a) => can(a.capability))
}

/** What each section mostly creates; that action leads the menu there. */
const SECTION_LEAD: Record<string, CreateAction['kind']> = {
  '/work': 'lead',
  '/calendar': 'job',
  '/inbox': 'text',
  '/customers': 'contact',
  '/contacts': 'contact',
  '/quotes': 'quote',
}

/**
 * Sections whose "+" only makes sense with one of these: on the Calendar the
 * "+" is for scheduling, so Crew who can't schedule a job or add an event
 * don't get it there (team-auth decision 5). Elsewhere the "+" stays (§21).
 */
const SECTION_NEEDS: Record<string, CapabilityName[]> = {
  '/calendar': ['scheduleJob', 'manageEvents'],
}

/**
 * The "+" is for creating work: a lead, quote, job, invoice, payment or text.
 * Crew (jobs, calendar, customer contact, start/complete, notes and photos)
 * can't create any of those, so they get no "+" on Home, Work, Calendar or
 * Inbox (UX-010). Contacts keeps it for them: adding a customer is in scope.
 */
const WORK_CREATE_KINDS: CreateAction['kind'][] = ['lead', 'quote', 'job', 'payment', 'invoice', 'text']

/** False when this section's "+" would offer nothing the person can do here. */
export function createMenuShownOn(pathname: string, can: (c: CapabilityName) => boolean): boolean {
  const section = '/' + (pathname.split('/')[1] ?? '')
  const createsWork = CREATE_ACTIONS.some((a) => WORK_CREATE_KINDS.includes(a.kind) && can(a.capability))
  // Contact-only roles keep the "+" on Contacts (the phone's only way to add one there).
  if (!createsWork) return SECTION_LEAD[section] === 'contact' && can('createContact')
  const needs = SECTION_NEEDS[section]
  return !needs || needs.some(can)
}

/**
 * Same list everywhere (so muscle memory holds), but the current page's object
 * comes first — closest to the thumb.
 */
export function orderCreateActions(actions: CreateAction[], pathname: string): CreateAction[] {
  const lead = SECTION_LEAD['/' + (pathname.split('/')[1] ?? '')]
  if (!lead) return actions
  const first = actions.find((a) => a.kind === lead)
  return first ? [first, ...actions.filter((a) => a !== first)] : actions
}

/** Trigger diameter; the dock row sizes the "+" slot by it. */
export const FAB_SIZE = 56
const SIZE = FAB_SIZE
const BUBBLE = 48 // option bubble diameter
const STEP = 64 // vertical distance between bubbles (16px gap keeps them separate circles)
const INSET = (SIZE - BUBBLE) / 2

// ── Suppression ─────────────────────────────────────────────────────────────
// A page that is itself a create flow (Take payment) mounts
// <SuppressCreateMenu/> so the "+" stays out of its way.
let suppressCount = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }
export function useCreateMenuSuppressed() {
  return useSyncExternalStore(subscribe, () => suppressCount > 0, () => false)
}

/** For a page that is itself a create flow: hides the "+" while mounted. */
export function SuppressCreateMenu() {
  useEffect(() => {
    suppressCount++; emit()
    return () => { suppressCount--; emit() }
  }, [])
  return null
}

/**
 * The phone "+", at the right end of the dock row, on every page (owner
 * decision §21: "Create (+) stays on every page"). BottomTabBar renders it
 * beside the tab pill inside the dock's own MobileDockShell, so it shares the
 * dock's safe-area inset and the space the dock reserves (--dock-h).
 *
 * It used to float in its own layer ~70px above the dock, over page content,
 * so at rest it covered whatever sat bottom-right on every page: a card's
 * service chip, "New opportunity", a settings switch, a row's call icon
 * (UX-111 / UX-093). In the dock row it covers nothing the dock doesn't, so it
 * no longer has to hide while the page scrolls either.
 */
export function DockCreateMenu() {
  const suppressed = useCreateMenuSuppressed()
  const moreOpen = useUIStore((s) => s.moreMenuOpen)
  const phoneOpen = useCallStore((s) => s.softphoneExpanded)
  const searchOpen = useUIStore((s) => s.searchOpen)
  const { can } = usePermissions()
  const { pathname } = useLocation()
  const fabRef = useRef<HTMLDivElement>(null)
  // Typing, a sheet open, or a keyboard-focused control under it (UX-111).
  const stepAside = useFabClearance(fabRef)
  if (suppressed || searchOpen || !createMenuShownOn(pathname, can) || allowedCreateActions(can).length === 0) return null
  // With the More card or the phone panel up the "+" has nothing to do, but
  // its slot stays so the tab pill keeps its width: no jump under the finger,
  // and the More card morphs back into the same pill.
  const parked = moreOpen || phoneOpen
  return (
    <div
      className="md:hidden shrink-0 flex items-center justify-center"
      style={{ width: FAB_SIZE, height: DOCK_PILL_H }}
      data-testid="dock-create-menu"
    >
      {/* Hidden in place, never unmounted: the row never reflows. */}
      {!parked && <div
        ref={fabRef}
        data-step-aside={stepAside ? '' : undefined}
        inert={stepAside || undefined}
        aria-hidden={stepAside || undefined}
        className={cn(
          'motion-safe:transition-[opacity,transform] motion-safe:duration-150 motion-safe:ease-out',
          stepAside ? 'pointer-events-none opacity-0 scale-90' : 'pointer-events-auto opacity-100',
        )}
      >
        <CreateMenu />
      </div>}
    </div>
  )
}

const FILL = '#FFFFFF'

export function CreateMenu() {
  const { can } = usePermissions()
  const { pathname } = useLocation()
  const actions = orderCreateActions(allowedCreateActions(can), pathname)
  const net = useNetworkAction()
  // Reduced motion: everything appears at once. The library snaps the
  // bubbles, but its per-item delays and the labels' transition delays still
  // stepped the menu in over ~800ms (UX-117).
  const reduced = useReducedMotion()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const menuId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const anchorRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ bottom: number; right: number } | null>(null)
  // `shown` = layer mounted. It goes up one frame before `open` so the
  // bubbles start at the plus and spring out, and stays until they have
  // merged back after close.
  const [shown, setShown] = useState(false)

  const toggle = () => {
    if (open) return setOpen(false)
    const r = anchorRef.current?.getBoundingClientRect()
    if (r) setAnchor({ bottom: window.innerHeight - r.bottom, right: window.innerWidth - r.right })
    setShown(true)
    // Reduced motion: open in the same render (nothing to animate from).
    if (reduced) setOpen(true)
    else requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)))
  }

  useEffect(() => {
    if (open || !shown) return
    const t = window.setTimeout(() => setShown(false), reduced ? 0 : 520)
    return () => window.clearTimeout(t)
  }, [open, shown, reduced])

  // The menu lives in a portal (above the header's blur/transform), pinned to
  // wherever the trigger currently sits.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[]
        const i = items.indexOf(document.activeElement as HTMLButtonElement)
        // The stack grows upward from the "+", so Up moves away from it.
        const next = e.key === 'ArrowUp' ? i + 1 : i - 1
        items[(next + items.length) % items.length]?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    // Focus the first option once it has started moving out.
    const t = window.setTimeout(() => itemRefs.current[0]?.focus({ preventScroll: true }), 60)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
    }
  }, [open])

  const choose = (to: string) => {
    setOpen(false)
    navigate(to)
  }

  // Bubbles stack upward from the "+"; item 0 (this page's object) is nearest.
  const stackH = SIZE + 14 + STEP * actions.length
  const overlay =
    shown &&
    anchor &&
    createPortal(
      <div className="md:hidden" data-testid="create-menu-layer" data-create-menu-layer="">
        {/* Scrim — tap outside closes */}
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className={cn(
            'fixed inset-0 z-[60] bg-black/55 backdrop-blur-[2px]',
            'motion-safe:transition-opacity motion-safe:duration-300',
            open ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        />
        <div
          className="fixed z-[61] pointer-events-none"
          style={{ bottom: anchor.bottom, right: anchor.right }}
        >
          <Liquid
            blur={6}
            contrast={20}
            fill={FILL}
            shadow="0 12px 30px -8px rgba(0,0,0,0.7)"
            style={{ width: SIZE, height: stackH }}
          >
            {/* Stationary blob under the trigger so bubbles split from it */}
            {/* Stationary blob over the header trigger; the bubbles split
                from it. Its button closes the menu (the real trigger sits
                under this layer while it is open). */}
            <Liquid.Item x={0} y={stackH - SIZE}>
              <div style={{ width: SIZE, height: SIZE, borderRadius: 9999 }}>
                <button
                  type="button"
                  onClick={() => { setOpen(false); triggerRef.current?.focus() }}
                  aria-label="Close create menu"
                  tabIndex={open ? 0 : -1}
                  className="pointer-events-auto w-14 h-14 rounded-full flex items-center justify-center text-black"
                  style={{ background: 'transparent', WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus
                    className={cn(
                      'w-6 h-6 motion-safe:transition-transform motion-safe:duration-300',
                      open && 'rotate-45',
                    )}
                    style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.25, 0.64, 1)' }}
                  />
                </button>
              </div>
            </Liquid.Item>
            <div id={menuId} role="menu" aria-label="Create new" aria-hidden={!open}>
              {actions.map(({ label, to, icon: Icon }, i) => (
                <Liquid.Item
                  key={to}
                  x={INSET}
                  y={open ? stackH - SIZE - 14 - STEP * i - BUBBLE : stackH - SIZE + INSET}
                  transition="bouncy"
                  delay={reduced ? 0 : open ? i * 35 : (actions.length - 1 - i) * 25}
                  style={{ position: 'absolute', top: 0, left: 0 }}
                >
                  <div className="relative" style={{ width: BUBBLE, height: BUBBLE, borderRadius: 9999 }}>
                    <button
                      ref={(el) => { itemRefs.current[i] = el }}
                      type="button"
                      role="menuitem"
                      tabIndex={open ? 0 : -1}
                      aria-label={label}
                      onClick={() => choose(to)}
                      className={cn(
                        'absolute inset-0 rounded-full flex items-center justify-center text-black',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                        open ? 'pointer-events-auto' : 'pointer-events-none',
                      )}
                      style={{ background: 'transparent', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Icon className="w-[21px] h-[21px]" />
                    </button>
                    {/* Visible label, left of the bubble */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap',
                        'rounded-full px-3 py-1.5 text-[14px] font-medium text-white',
                        'motion-safe:transition-[opacity,transform] motion-safe:duration-300',
                        open ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2',
                      )}
                      style={{
                        background: 'rgba(24,24,28,0.92)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        transitionDelay: open && !reduced ? `${120 + i * 35}ms` : '0ms',
                      }}
                    >
                      {label}
                    </span>
                  </div>
                </Liquid.Item>
              ))}
            </div>
          </Liquid>
        </div>
      </div>,
      document.body,
    )

  // Nothing this person can create (a Viewer): no "+" at all.
  if (actions.length === 0) return null

  return (
    <div ref={anchorRef} className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        {...net()}
        aria-label={open ? 'Close create menu' : 'Create new'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        data-testid="create-menu-button"
        className="relative w-14 h-14 rounded-full flex items-center justify-center bg-white text-black active:scale-95 motion-safe:transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          WebkitTapHighlightColor: 'transparent',
          boxShadow: '0 14px 34px -10px rgba(0,0,0,0.75), 0 3px 10px rgba(0,0,0,0.45)',
        }}
      >
        <Plus
          className={cn(
            'w-6 h-6 motion-safe:transition-transform motion-safe:duration-300',
            open && 'rotate-45',
          )}
          style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.25, 0.64, 1)' }}
        />
      </button>
      {overlay}
    </div>
  )
}
